/**
 * Component for PaletteNavigationCard
 *
 * Adapter component responsible for transforming
 * data into props consumable by the web-palette
 * NavigationCardDefaultContent component.
 *
 * @module ui/modules/PaletteNavigationCard
 * @memberof -Common
 */

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  NavigationCardDefaultContent as PalNavigationCardDefaultContent
} from 'web-palette/dist/components/NavigationCardDefaultContent/NavigationCardDefaultContent';


/**
 * Represents a PaletteNavigationCard adapter
 *
 * @method
 * @param {PaletteNavigationCardProps} props
 * @returns {JSX.Element}
 */
export const PaletteNavigationCard = function( props ){
  const {
    backgroundColor,
    borderColor,
    externalMediaElement,
    iconName,
    textOnlyLabel,
    actionLabel,
    customClassName
  } = props;


  return (
    <div
      className={ classNames( 'PaletteNavigationCard', {
        [ `${customClassName}` ]: customClassName
      } ) }
    >
      <PalNavigationCardDefaultContent
        backgroundColor={ backgroundColor }
        borderColor={ borderColor }
        externalMediaElement={ externalMediaElement }
        iconName={ iconName }
        actionLabel={ actionLabel }
        textOnlyLabel={ textOnlyLabel }
      />
    </div>
  );
};

/**
 * Property type definitions
 * @typedef NavigationCardDefaultProps
 * @type {object}
 * @property {string} backgroundColor - Accepts background color to set for the NavigationCard circle container
 * @property {string} borderColor - Accepts border color to set for the NavigationCard circle container
 * @property {object} externalMediaElement - Accepts UltaAsset image from web-site
 * @property {string} iconName - Sets the name of the branded icon to render inside the Navigation card circle
 * @property {string} textOnlyLabel - Accepts text to display inside the Navigation card circle ( e.g. Shop all, View all )
 * @property {string} actionLabel - Accepts label text of the link from the web-site
 * @property {string} customClassName - Accepts customClassName to adjust the container
 */
export const propTypes =  {
  /**
   * Accepts background color to set for the NavigationCard circle container
   */
  backgroundColor: PropTypes.string,
  /**
   * Accepts border color to set for the NavigationCard circle container
   */
  borderColor: PropTypes.string,
  /**
   * Accepts UltaAsset image from web-site
   */
  externalMediaElement: PropTypes.object,
  /**
   * Sets the name of the branded icon to render inside the Navigation card circle
   */
  iconName: PropTypes.string,
  /**
   * Accepts text to display inside the Navigation card circle ( e.g. Shop all, View all )
   */
  textOnlyLabel: PropTypes.string,
  /**
   * Accepts label text of the link from the web-site
   */
  actionLabel: PropTypes.string,
  /**
   * Accepts customClassName to adjust the container
   */
  customClassName: PropTypes.string
};

export default PaletteNavigationCard;
